import { merge } from "lodash"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useReducer } from "react"

import { SignContractWizardContext } from "./SignContractWizardContext"
import getSteps from "./Steps/steps"
import { kindForPaymentMethod } from "./paymentSelection"
import signContractWizardReducer, {
  DefaultState,
} from "./signContractWizardReducer"

export const SignContractWizardProvider = ({ children, ...props }) => {
  const {
    agreementOverview,
    billingSummaryHtml,
    boat,
    contact,
    contractChatUrl,
    customFieldDefinitions,
    isGuest,
    marina,
    postSubmitUrl,
    presentmentCurrency,
    pricingSummary,
    pricingSummaryHtml,
    quote,
    reachOut,
    savedPdfUrl,
    downloadPdfUrl,
    mobileWebview,
    userCards: paymentMethods,
    docusealSubmitterSlug,
    docusealFormCompleted,
    dockwaLogo,
    withTemplate,
    surcharge,
    surchargeCopy,
    wizardCopy,
  } = props

  const quoteStorageKey = useMemo(
    () => `signContractWizardState:${quote.encodedId}`,
    [quote]
  )

  const quoteWithWizardCopy = useMemo(
    () => ({ ...quote, wizardCopy }),
    [quote, wizardCopy]
  )

  const steps = useMemo(
    () => getSteps(quoteWithWizardCopy, withTemplate, agreementOverview),
    [quoteWithWizardCopy, withTemplate, agreementOverview]
  )

  const initialState = useMemo(() => {
    const { declinedAt, expiredAt } = quote
    const declined = declinedAt !== null
    const expired = expiredAt !== null

    // Pre-populate insurance and registration data from the boat if it exists
    const existingInsurance = boat?.insurance || {}
    const existingRegistration = boat?.registration || {}

    const stateFromData = {
      paymentMethods,
      declined,
      expired,
      docusealFormCompleted,
      // Pre-populate with existing data, but keep document flags false since we can't pre-populate file inputs
      requiredInformation: {
        phone: contact?.phone || "",
        registration: {
          registrationNumber: existingRegistration.number || "",
          expirationDate: existingRegistration.expirationDate || null,
          document: false, // File inputs can't be pre-populated
          documentError: null,
          documentValidating: false,
          existingDocument: existingRegistration.documentUrl
            ? {
                url: existingRegistration.documentUrl,
                previewUrl: existingRegistration.documentPreviewImageUrl,
                name: existingRegistration.documentName,
              }
            : null,
        },
        insurance: {
          companyName: existingInsurance.companyName || "",
          policyNumber: existingInsurance.policyNumber || "",
          expirationDate: existingInsurance.expirationDate || null,
          document: false, // File inputs can't be pre-populated
          documentError: null,
          documentValidating: false,
          existingDocument: existingInsurance.documentUrl
            ? {
                url: existingInsurance.documentUrl,
                previewUrl: existingInsurance.documentPreviewImageUrl,
                name: existingInsurance.documentName,
              }
            : null,
        },
      },
    }

    const savedState = localStorage.getItem(quoteStorageKey)
      ? JSON.parse(localStorage.getItem(quoteStorageKey))
      : {}

    const restoredState = merge({}, DefaultState, savedState, stateFromData)
    const state = {
      ...restoredState,
      paymentMethodKind:
        restoredState.paymentMethodKind ||
        kindForPaymentMethod(
          restoredState.paymentMethod,
          restoredState.paymentMethods
        ),
    }

    // if we are here we need to recalculate the first invalid step based on the state. a user may have
    // completed a step and then refreshed the page, which would result in the loss of attached
    // documents.
    let currentStep = 1
    while (
      currentStep < steps.length &&
      steps[currentStep - 1].validate(state)
    ) {
      currentStep++
    }

    // the form should always start on the current valid step, and documents are not persisted to state so will always
    // begin as false
    const stateOverrides = {
      currentStep,
      furthestStepReached: Math.max(
        state.furthestStepReached ?? 1,
        currentStep
      ),
    }

    return merge({}, state, stateOverrides)
  }, [
    paymentMethods,
    quote,
    quoteStorageKey,
    steps,
    boat,
    contact,
    docusealFormCompleted,
  ])

  const [state, dispatch] = useReducer(signContractWizardReducer, initialState)

  useEffect(() => {
    const { declined, expired } = state

    if (declined) {
      localStorage.setItem(quoteStorageKey, JSON.stringify({ declined }))
    } else if (expired) {
      localStorage.setItem(quoteStorageKey, JSON.stringify({ expired }))
    } else {
      const {
        review,
        submitFailure,
        reviewFallback,
        paymentMethodError,
        ...persistedState
      } = state

      try {
        localStorage.setItem(quoteStorageKey, JSON.stringify(persistedState))
      } catch (error) {
        console.error(error)
      }
    }
  }, [state, quote, quoteStorageKey])

  const customFieldDefinitionsById = customFieldDefinitions.reduce(
    (map, customFieldDefinition) => {
      map[customFieldDefinition.id] = customFieldDefinition
      return map
    },
    {}
  )

  return (
    <SignContractWizardContext.Provider
      value={{
        agreementOverview,
        billingSummaryHtml,
        boat,
        contact,
        contractChatUrl,
        customFieldDefinitions: customFieldDefinitionsById,
        dispatch,
        isGuest,
        marina,
        postSubmitUrl,
        presentmentCurrency,
        pricingSummary,
        pricingSummaryHtml,
        quote: quoteWithWizardCopy,
        reachOut,
        savedPdfUrl,
        downloadPdfUrl,
        mobileWebview,
        state,
        steps,
        docusealSubmitterSlug,
        dockwaLogo,
        surcharge,
        surchargeCopy,
        withTemplate,
        wizardCopy,
      }}
    >
      {children}
    </SignContractWizardContext.Provider>
  )
}

SignContractWizardProvider.propTypes = {
  boat: PropTypes.shape({
    name: PropTypes.string,
    lengthOverall: PropTypes.number,
    beam: PropTypes.number,
    insurance: PropTypes.shape({
      companyName: PropTypes.string,
      policyNumber: PropTypes.string,
      expirationDate: PropTypes.string,
      documentUrl: PropTypes.string,
      documentPreviewImageUrl: PropTypes.string,
      documentEncodedId: PropTypes.string,
      documentName: PropTypes.string,
    }),
    registration: PropTypes.shape({
      number: PropTypes.string,
      expirationDate: PropTypes.string,
      documentUrl: PropTypes.string,
      documentPreviewImageUrl: PropTypes.string,
      documentEncodedId: PropTypes.string,
      documentName: PropTypes.string,
    }),
  }).isRequired,
  children: PropTypes.node.isRequired,
  contact: PropTypes.shape({
    name: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  contractChatUrl: PropTypes.string.isRequired,
  customFieldDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      displayOrder: PropTypes.number.isRequired,
      required: PropTypes.bool.isRequired,
    })
  ).isRequired,
  agreementOverview: PropTypes.bool.isRequired,
  billingSummaryHtml: PropTypes.string,
  isGuest: PropTypes.bool.isRequired,
  marina: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string,
  }).isRequired,
  postSubmitUrl: PropTypes.string.isRequired,
  presentmentCurrency: PropTypes.string.isRequired,
  reachOut: PropTypes.shape({
    design: PropTypes.oneOf(["legacy", "needHelp"]).isRequired,
    mode: PropTypes.oneOf(["message", "call", "none"]).isRequired,
    phone: PropTypes.string,
    phoneUri: PropTypes.string,
  }).isRequired,
  pricingSummaryHtml: PropTypes.string,
  pricingSummary: PropTypes.shape({
    totalDueNow: PropTypes.number.isRequired,
    displayTotalDueNow: PropTypes.string.isRequired,
  }).isRequired,
  quote: PropTypes.shape({
    id: PropTypes.number.isRequired,
    encodedId: PropTypes.string.isRequired,
    contactEncodedId: PropTypes.string,
    spaceType: PropTypes.oneOf(["dock", "mooring"]),
    pricingStructure: PropTypes.string.isRequired,
    declinedAt: PropTypes.string,
    expiredAt: PropTypes.string,
    contractSigningEnablements: PropTypes.arrayOf(
      PropTypes.shape({
        customFieldDefinitionId: PropTypes.number.isRequired,
        required: PropTypes.bool.isRequired,
      })
    ),
  }).isRequired,
  savedPdfUrl: PropTypes.string.isRequired,
  downloadPdfUrl: PropTypes.string.isRequired,
  mobileWebview: PropTypes.bool.isRequired,
  userCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      stripePaymentMethodId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  docusealSubmitterSlug: PropTypes.string,
  docusealFormCompleted: PropTypes.bool,
  dockwaLogo: PropTypes.string,
  withTemplate: PropTypes.bool.isRequired,
  surcharge: PropTypes.shape({
    eligible: PropTypes.bool,
    ratePercent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    notice: PropTypes.string,
    creditNotice: PropTypes.string,
  }),
  surchargeCopy: PropTypes.string,
  wizardCopy: PropTypes.shape({
    back: PropTypes.string.isRequired,
    backToPaymentMethod: PropTypes.string,
    continue: PropTypes.string,
    continueTo: PropTypes.string,
    insuranceRequirementsActivePolicy: PropTypes.string,
    insuranceRequirementsActivePolicyDetail: PropTypes.string,
    insuranceRequirementsHeading: PropTypes.string,
    insuranceRequirementsVisible: PropTypes.string,
    insuranceRequirementsVisibleDetail: PropTypes.string,
    insuranceSubtitle: PropTypes.string,
    insuranceTitle: PropTypes.string,
    insuranceUpload: PropTypes.string,
    registrationRequirementsCurrent: PropTypes.string,
    registrationRequirementsCurrentDetail: PropTypes.string,
    registrationRequirementsHeading: PropTypes.string,
    registrationRequirementsVisible: PropTypes.string,
    registrationRequirementsVisibleDetail: PropTypes.string,
    registrationUpload: PropTypes.string,
    signatureUnconfirmed: PropTypes.string.isRequired,
    stepOf: PropTypes.string.isRequired,
  }).isRequired,
}
